import { Controller } from "@hotwired/stimulus"
import { PriceRange } from "@/lib/Listing/PriceRange.js"

// Range track doesn't reach the container edges — matches the CSS gap.
const TRACK_PERCENT = 92

export default class extends Controller {
  static targets = ["minSlider", "maxSlider", "activeLine", "minLabel", "maxLabel", "minHiddenInput", "maxHiddenInput"]
  static values = {
    min: Number,
    max: Number,
    step: Number,
    gap: Number,
    currency: String,
    initialMin: Number,
    initialMax: Number,
  }

  connect() {
    this.range = new PriceRange({ min: this.minValue, max: this.maxValue, step: this.stepValue, gap: this.gapValue })
    this.#applySliderState(this.range.sliderState(this.initialMinValue, this.initialMaxValue))
    this.#render()
    this.element.addEventListener("price-range-slider:reset", () => this.reset())
  }

  // The dragged handle yields when the two get closer than the gap.
  restrict({ target }) {
    this.isActive = true
    if (target === this.minSliderTarget) {
      this.minSliderTarget.value = this.range.clampMin(this.#currentMin, this.#currentMax)
    } else {
      this.maxSliderTarget.value = this.range.clampMax(this.#currentMin, this.#currentMax)
    }
    this.#render()
  }

  applyFilter() {
    // A custom range and the preset price[] checkboxes are mutually exclusive.
    this.#clearPresetPriceCheckboxes()

    const { min, max } = this.range.submitValues(this.#currentMin, this.#currentMax)
    this.minHiddenInputTarget.value = min
    this.maxHiddenInputTarget.value = max
    this.isActive = !this.range.isFullRange(this.#currentMin, this.#currentMax)
    this.#render()

    this.minHiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  reset() {
    this.#applySliderState(this.range.sliderState(0, 0))
    this.minHiddenInputTarget.value = ""
    this.maxHiddenInputTarget.value = ""
    this.#render()
  }

  get #currentMin() {
    return this.minSliderTarget.valueAsNumber
  }

  get #currentMax() {
    return this.maxSliderTarget.valueAsNumber
  }

  #applySliderState({ min, max, active }) {
    this.minSliderTarget.value = min
    this.maxSliderTarget.value = max
    this.isActive = active
  }

  #clearPresetPriceCheckboxes() {
    this.element
      .closest("form")
      .querySelectorAll("input[name='price[]']")
      .forEach((checkbox) => (checkbox.checked = false))
  }

  #render() {
    this.minLabelTarget.textContent = this.range.label(this.#currentMin, this.currencyValue)
    this.maxLabelTarget.textContent = this.range.label(this.#currentMax, this.currencyValue)

    for (const label of [this.minLabelTarget, this.maxLabelTarget]) {
      label.classList.toggle("text-zinc-700", this.isActive)
      label.classList.toggle("text-zinc-300", !this.isActive)
    }

    const { left, right } = this.range.trackPercents(this.#currentMin, this.#currentMax, TRACK_PERCENT)
    this.activeLineTarget.style.left = `calc(${left}%)`
    this.activeLineTarget.style.right = `calc(${right}%)`
    this.activeLineTarget.classList.toggle("hidden", !this.isActive)
    this.element.classList.toggle("is-active", this.isActive)
  }
}
